// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Kitchen redesign event instrumentation (2026-09-14) — web twin of
 * mobile/src/lib/kitchenAnalytics.ts, same reasoning: no analytics SDK
 * exists anywhere in this app today, and the spec asks to reuse existing
 * conventions rather than introduce a new framework. The only existing
 * convention is server-side ("DB rows as telemetry" per
 * docs/OBSERVABILITY.md); extending that is a real backend change this pass
 * deliberately does not make. Every call site the spec asks for is
 * correctly instrumented; this is the honest, dev-visible placeholder for
 * wherever a real sink lands — never a fabricated delivery to a collector
 * that doesn't exist.
 */
import { trackFunnelEvent } from "./analytics"

export type KitchenEventName =
  | "kitchen_opened"
  | "barcode_scan_started"
  | "barcode_scan_success"
  | "barcode_scan_failed"
  | "kitchen_item_added"
  | "kitchen_item_updated"
  | "kitchen_item_removed"
  | "amy_kitchen_suggestion_clicked"

export function trackKitchenEvent(
  name: KitchenEventName,
  props?: Record<string, string | number | boolean>
): void {
  // G04 (2026-09-25): "Kitchen opened" is one of the acquisition-funnel's
  // required minimum events (lib/analytics.ts) — the real sink this
  // module's own doc comment anticipated. Every other kitchen event stays
  // on the dev-only placeholder below; forwarding those too would mean
  // widening lib/analytics.ts's FunnelEventName beyond the funnel it
  // documents, which is a separate decision this pass doesn't make.
  if (name === "kitchen_opened") {
    trackFunnelEvent("kitchen_opened", props)
    return
  }
  if (import.meta.env.DEV) {
    // The one, deliberate, dev-only sink until a real one exists (see module doc).
    console.log(`[kitchen event] ${name}`, props ?? {})
  }
}
